import React, { useEffect, useState } from "react"
import { ScissorOutlined, FolderOpenOutlined, FolderOutlined } from "@ant-design/icons"
import { Button, Image, Space, Table, Tooltip, Typography, message, Tag } from "antd"
import moment from "moment"
import axiosInstance from "../../../axios/axiosConfig"
import { collection, doc, onSnapshot, query, where } from "firebase/firestore"
import { db, storage } from "../../../firebase/config"
import { getDownloadURL, listAll, ref, getMetadata } from "firebase/storage"
import { loadingSpinWithBg } from "../../../components/Elements/LoadingSpinner"
import { ASAPOrder, OrderAndPrefix } from "../../../components/Orders/Helpers/OrderAndPrefix"

const { Text } = Typography

// --- Expanded row component to show file list for each order ---
function ExpandedRowFiles({ record }) {
  const [files, setFiles] = useState([])
  const [loadingFiles, setLoadingFiles] = useState(true)

  useEffect(() => {
    // Construct folder path based on order's selected plate code
    const folderLocation = `/vivaPlates/VIVA${record.selectedPlate.codeNumber}/`
    const folderRef = ref(storage, folderLocation)
    listAll(folderRef)
      .then((res) => {
        // Filter out any files ending with ".keep"
        const validItems = res.items.filter((item) => !item.name.endsWith(".keep"))
        // Fetch metadata for each valid file to retrieve updated date
        Promise.all(
          validItems.map((item) =>
            getMetadata(item).then((metadata) => ({
              name: item.name,
              fullPath: item.fullPath,
              updated: metadata.updated,
            }))
          )
        )
          .then((filesData) => {
            setFiles(filesData)
            setLoadingFiles(false)
          })
          .catch((error) => {
            console.error("Error fetching metadata:", error)
            setLoadingFiles(false)
          })
      })
      .catch((error) => {
        console.error("Error listing files:", error)
        setLoadingFiles(false)
      })
  }, [record])

  // Function to open a single file in a new tab
  const openFile = (fileName, fileFullPath) => {
    const fileRef = ref(storage, fileFullPath)
    getDownloadURL(fileRef)
      .then((url) => {
        window.open(url, "_blank")
      })
      .catch((error) => {
        message.error("Error opening file")
        console.error("Error opening file:", error)
      })
  }

  if (loadingFiles) {
    return <div>Loading files...</div>
  }
  if (files.length === 0) {
    return <div>No files found.</div>
  }
  return (
    <div>
      {files.map((file) => (
        <Space key={file.fullPath} style={{ marginBottom: 8 }}>
          <Text>{file.name}</Text>
          <Tag color="success">{new Date(file.updated).toLocaleString("lv-LV")}</Tag>
          <Button type="link" onClick={() => openFile(file.name, file.fullPath)}>
            Open
          </Button>
        </Space>
      ))}
    </div>
  )
}

// --- Main component with Table ---
export default function VivaPlatesOrders() {
  const [vivaPrintData, setVivaPrintData] = useState([])
  const [vivaSequence, setVivaSequence] = useState(vivaPrintData.map((item) => item.id))
  const [isSorting, setIsSorting] = useState(true)
  const [downloadLoading, setDownloadLoading] = useState(false)

  let clientFIlters =
    vivaPrintData &&
    vivaPrintData.reduce((accumulator, item) => {
      const clientName = item.client.client
      const existingItem = accumulator.find((filterItem) => filterItem.text === clientName)
      if (existingItem) {
        existingItem.value = clientName
      } else {
        accumulator.push({
          text: clientName,
          value: clientName,
        })
      }
      return accumulator
    }, [])

  let productFilters =
    vivaPrintData &&
    vivaPrintData.reduce((accumulator, item) => {
      const productName = item.selectedProduct.product.productName
      const existingItem = accumulator.find((filterItem) => filterItem.text === productName)
      if (existingItem) {
        existingItem.value = productName
      } else {
        accumulator.push({
          text: productName,
          value: productName,
        })
      }
      return accumulator
    }, [])

  let materialFilter =
    vivaPrintData &&
    vivaPrintData.reduce((accumulator, item) => {
      const materialName = item.selectedProduct.material
      const existingItem = accumulator.find((filterItem) => filterItem.text === materialName)
      if (existingItem) {
        existingItem.value = materialName
      } else {
        accumulator.push({
          text: materialName,
          value: materialName,
        })
      }
      return accumulator
    }, [])

  // Multi-file download function (if needed)
  const handleDownload = async (justNumber) => {
    setDownloadLoading(true)
    const folderLocation = `/vivaPlates/${"VIVA" + justNumber}/`
    const folderRef = ref(storage, folderLocation)

    try {
      const res = await listAll(folderRef)
      if (res.items.length === 0) {
        message.warning("No files found in the folder.")
        setDownloadLoading(false)
        return
      }

      const downloadPromises = res.items.map(async (itemRef) => {
        const url = await getDownloadURL(itemRef)
        const fileName = itemRef.name
        const xhr = new XMLHttpRequest()
        xhr.responseType = "blob"
        xhr.onload = () => {
          const blob = xhr.response
          const link = document.createElement("a")
          link.href = window.URL.createObjectURL(blob)
          link.download = fileName
          link.addEventListener("click", () => {
            setDownloadLoading(false)
            message.success("All files downloaded successfully.")
          })
          document.body.appendChild(link)
          link.click()
          document.body.removeChild(link)
        }
        xhr.onerror = () => {
          message.error(`Failed to download ${fileName}`)
          setDownloadLoading(false)
        }
        xhr.open("GET", url)
        xhr.send()
      })

      await Promise.all(downloadPromises)
    } catch (error) {
      console.error("Error downloading files: ", error)
      message.error("Failed to download files.")
      setDownloadLoading(false)
    }
  }

  const vivaPrintingCols = [
    {
      title: "Nr.",
      dataIndex: "orderNumber",
      align: "center",
      sorter: (a, b) => a.justOrderNumber - b.justOrderNumber,
      render: (value, record) => (
        <Space>
          {ASAPOrder(record)}
          {OrderAndPrefix(record)}
        </Space>
      ),
    },
    {
      title: "Created",
      dataIndex: "created",
      align: "center",
      sorter: (a, b) => a.created.seconds - b.created.seconds,
      render: (value) => <Text>{moment.unix(value.seconds).format("DD.MM.YYYY")}</Text>,
    },
    {
      title: "Client",
      dataIndex: ["client", "client"],
      align: "center",
      ellipsis: { showTitle: false },
      filters: clientFIlters,
      onFilter: (value, record) => record.client.client.startsWith(value),
      filterSearch: true,
      render: (item) => (
        <Tooltip placement="topLeft" title={item}>
          {item}
        </Tooltip>
      ),
    },
    {
      title: "Product",
      dataIndex: ["selectedProduct", "product", "productName"],
      align: "center",
      filters: productFilters,
      onFilter: (value, record) => record.selectedProduct.product.productName === value,
      filterSearch: true,
      ellipsis: { showTitle: false },
      render: (item) => (
        <Tooltip placement="topLeft" title={item}>
          {item}
        </Tooltip>
      ),
    },
    {
      title: "Quantity",
      dataIndex: "labelCount",
      align: "center",
      width: "7%",
      sorter: (a, b) => a.labelCount - b.labelCount,
    },
    {
      title: "Die-cut",
      dataIndex: ["selectedProduct", "knifeCode"],
      align: "center",
    },
    {
      title: "Width",
      dataIndex: ["selectedProduct", "labelWidth"],
      align: "center",
      width: "5%",
    },
    {
      title: "Height",
      dataIndex: ["selectedProduct", "labelHeight"],
      align: "center",
      width: "5%",
    },
    {
      title: "M.Width",
      dataIndex: ["selectedProduct", "formatWidth"],
      align: "center",
      sorter: (a, b) => a.selectedProduct.formatWidth - b.selectedProduct.formatWidth,
      render: (value, record) =>
        record.readRollWidth ? <Text>{record.rollWidth}</Text> : <Text>{value}</Text>,
    },
    {
      title: "Proj. M.",
      dataIndex: ["projectedMeters"],
      align: "center",
      width: "7%",
      render: (value) => <Text>{value}m</Text>,
    },
    {
      title: "Material",
      dataIndex: ["selectedProduct", "material"],
      align: "center",
      filters: materialFilter,
      onFilter: (value, record) => record.selectedProduct.material.startsWith(value),
      filterSearch: true,
      ellipsis: { showTitle: false },
      render: (item) => (
        <Tooltip placement="topLeft" title={item}>
          {item}
        </Tooltip>
      ),
    },
    {
      title: "Plate",
      dataIndex: ["selectedPlate", "code"],
      align: "center",
      ellipsis: { showTitle: false },
      render: (item) =>
        item ? (
          <Tooltip placement="topLeft" title={item}>
            {item}
          </Tooltip>
        ) : (
          <ScissorOutlined style={{ color: "#f5222d" }} />
        ),
    },
    {
      title: "Technical",
      dataIndex: "technicalPage",
      align: "center",
      render: (value) => <Image src={value} />,
    },
  ]

  // Get/update the vivaSequence from Firestore.
  useEffect(() => {
    const sequenceDocRef = doc(db, "sequence", "documentForVivaSequence")
    const unsubscribe = onSnapshot(sequenceDocRef, (doc) => {
      if (doc.exists()) {
        const sequenceData = doc.data()
        setVivaSequence(sequenceData.vivaSequence)
      }
    })
    return () => unsubscribe()
  }, [])

  // Fetch orders from Firestore and sort them based on vivaSequence.
  useEffect(() => {
    const ordersRef = collection(db, "orders")
    let q = query(
      ordersRef,
      where("orderStatus", "==", 4),
      where("selectedProduct.machine", "==", "Viva")
    )
    const unsubscribe = onSnapshot(q, async (snapshot) => {
      let orders = snapshot.docs.map((doc) => ({
        ...doc.data(),
        key: doc.id,
        id: doc.id,
      }))

      // Fetch additional knife data and calculate projected meters.
      const ordersWithKnifeData = await Promise.all(
        orders.map(async (order) => {
          if (
            order.selectedProduct.knifeCode === undefined ||
            order.selectedProduct.knifeCode === "none"
          ) {
            return { ...order, projectedMeters: 0 }
          }
          try {
            const response = await axiosInstance.get(
              `/api/searchByCode?code=${encodeURIComponent(order.selectedProduct.knifeCode)}`
            )
            const knife = response.data
            const countInThousands = order.labelCount / 1000
            const a1 = (knife.height + knife.gapRaport) * 0.001
            const a2 = (1000 / knife.rowss).toFixed(2)
            const a3 = Math.ceil(a1 * a2)
            const a4 = a3 * countInThousands
            return { ...order, projectedMeters: a4 }
          } catch (error) {
            console.error("Failed to fetch knife data:", error)
            return { ...order, projectedMeters: 0 }
          }
        })
      )

      const sortedOrders = ordersWithKnifeData.sort((a, b) => {
        return vivaSequence.indexOf(a.id) - vivaSequence.indexOf(b.id)
      })

      setVivaPrintData(sortedOrders)
      setIsSorting(false)
    })
    return () => unsubscribe()
  }, [vivaSequence])

  return (
    <div>
      <Table
        bordered
        size="small"
        className="custom-table"
        rowKey={(record) => record.id}
        dataSource={vivaPrintData}
        loading={isSorting}
        columns={vivaPrintingCols}
        rowClassName={(record) => (record.priorityOrder ? "red-background" : "")}
        pagination={{ pageSize: 40, showSizeChanger: true }}
        expandable={{
          expandedRowRender: (record) => <ExpandedRowFiles record={record} />,
          expandIcon: ({ expanded, onExpand, record }) =>
            expanded ? (
              <FolderOpenOutlined
                onClick={(e) => onExpand(record, e)}
                style={{ fontSize: "18px", marginRight: 8 }}
              />
            ) : (
              <FolderOutlined
                onClick={(e) => onExpand(record, e)}
                style={{ fontSize: "18px", marginRight: 8 }}
              />
            ),
          rowExpandable: (record) => !!record.selectedPlate?.code,
        }}
      />

      {downloadLoading ? loadingSpinWithBg(75, "Downloading File") : ""}
    </div>
  )
}
